import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Column } from '../../enum/Column';
import { GeoColumns } from '../../enum/GeoDivisionColumnName';

export interface ColumnOrderState {
  columns: Column[];
}

const initialState: ColumnOrderState = {
  columns: [],
};

export const columnSlice = createSlice({
  name: 'columnSlice',
  initialState,
  reducers: {
    setColumns(state, action: PayloadAction<string[]>) {
      state.columns = [];

      // Convert enum values to string array
      const geoLocked = Object.values(GeoColumns) as string[];

      // Add CCY manually as CCY is not part of GeoColumns but should remain locked
      const lockedColumns = new Set<string>([...geoLocked, 'CCY']);

      action.payload.forEach((column) => {
        if (column !== 'PaymentSource' && column !== 'LocationID') {
          state.columns.push({
            name: column,
            isVisible: true,
            isLocked: lockedColumns.has(column),
            isPinned: false,
          });
        }
      });
    },

    togglePin(state, action: PayloadAction<string>) {
      const columnName = action.payload;

      const column = state.columns.find((c) => c.name === columnName);
      if (column) {
        column.isPinned = !column.isPinned;
      }
    },

    toggleVisible(state, action: PayloadAction<string>) {
      const columnName = action.payload;

      const column = state.columns.find((c) => c.name === columnName);
      if (column) {
        column.isVisible = !column.isVisible;
      }
    },

    moveColumn(state, action: PayloadAction<{ from: number; to: number }>) {
      const { from, to } = action.payload;

      const locked = state.columns.filter((c) => c.isLocked);
      const pinned = state.columns.filter((c) => !c.isLocked && c.isPinned);
      const unpinned = state.columns.filter((c) => !c.isLocked && !c.isPinned);

      const unlocked = [...pinned, ...unpinned];

      const dragged = unlocked[from];
      if (!dragged) return;

      if (dragged.isPinned) {
        if (to < 0 || to >= pinned.length) return;

        const updatedPinned = [...pinned];
        const [moved] = updatedPinned.splice(from, 1);
        updatedPinned.splice(to, 0, moved);

        state.columns = [...locked, ...updatedPinned, ...unpinned];
        return;
      }

      const unpinnedFrom = from - pinned.length;
      const unpinnedTo = to - pinned.length;

      if (unpinnedFrom < 0 || unpinnedFrom >= unpinned.length) return;
      if (unpinnedTo < 0 || unpinnedTo >= unpinned.length) return;

      const updatedUnpinned = [...unpinned];
      const [moved] = updatedUnpinned.splice(unpinnedFrom, 1);
      updatedUnpinned.splice(unpinnedTo, 0, moved);

      state.columns = [...locked, ...pinned, ...updatedUnpinned];
    },
  },
});

export const { setColumns, togglePin, toggleVisible, moveColumn } = columnSlice.actions;

export default columnSlice.reducer;
